/**
 * Bulk Actions Toolbar
 *
 * Appears when jobs are selected. Provides bulk operation actions.
 * PURELY PRESENTATIONAL - uses hooks for operations.
 *
 * DDD Compliance:
 * - No business logic in component
 * - Uses application layer hooks for operations
 * - Handles UI state only
 */

import { useState } from 'react';
import { useBulkStartJobs, useBulkCompleteJobs, useBulkCancelJobs } from '@/application/service-provider/hooks/useBulkJobOperations';
import { exportSelectedJobsToCSV } from '@/lib/csvExport';
import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';

interface BulkActionsToolbarProps {
  selectedJobIds: Set<string>;
  allJobs: JobCardViewModel[];
  onClearSelection: () => void;
}

export function BulkActionsToolbar({
  selectedJobIds,
  allJobs,
  onClearSelection,
}: BulkActionsToolbarProps) {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const bulkStart = useBulkStartJobs();
  const bulkComplete = useBulkCompleteJobs();
  const bulkCancel = useBulkCancelJobs();

  const selectedCount = selectedJobIds.size;
  const selectedJobsArray = Array.from(selectedJobIds);

  const handleBulkStart = async () => {
    if (confirm(`Start ${selectedCount} selected jobs?`)) {
      await bulkStart.mutateAsync(selectedJobsArray);
      onClearSelection();
    }
  };

  const handleBulkComplete = async () => {
    if (confirm(`Mark ${selectedCount} selected jobs as complete?`)) {
      await bulkComplete.mutateAsync(selectedJobsArray);
      onClearSelection();
    }
  };

  const handleBulkCancel = async () => {
    setShowCancelModal(true);
  };

  const confirmBulkCancel = async () => {
    if (!cancelReason.trim()) {
      alert('Please provide a cancellation reason');
      return;
    }

    await bulkCancel.mutateAsync({
      jobIds: selectedJobsArray,
      reason: cancelReason,
    });

    setShowCancelModal(false);
    setCancelReason('');
    onClearSelection();
  };

  const handleExportCSV = () => {
    exportSelectedJobsToCSV(allJobs, selectedJobIds);
  };

  const isLoading = bulkStart.isPending || bulkComplete.isPending || bulkCancel.isPending;

  return (
    <>
      <div className="card bg-blue-50 border-blue-200">
        <div className="card-content">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedCount} job{selectedCount !== 1 ? 's' : ''} selected
              </span>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleBulkStart}
                  disabled={isLoading}
                  className="btn-sm btn-primary"
                >
                  {bulkStart.isPending ? 'Starting...' : 'Start Selected'}
                </button>

                <button
                  onClick={handleBulkComplete}
                  disabled={isLoading}
                  className="btn-sm btn-secondary"
                >
                  {bulkComplete.isPending ? 'Completing...' : 'Complete Selected'}
                </button>

                <button
                  onClick={handleBulkCancel}
                  disabled={isLoading}
                  className="btn-sm btn-outline text-red-600 hover:bg-red-50 hover:border-red-300"
                >
                  {bulkCancel.isPending ? 'Cancelling...' : 'Cancel Selected'}
                </button>

                <button
                  onClick={handleExportCSV}
                  disabled={isLoading}
                  className="btn-sm btn-outline"
                >
                  Export to CSV
                </button>
              </div>
            </div>

            <button
              onClick={onClearSelection}
              disabled={isLoading}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear Selection
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card max-w-md w-full mx-4">
            <div className="card-content">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                Cancel {selectedCount} Jobs
              </h3>

              <p className="text-sm text-neutral-600 mb-4">
                Please provide a reason for cancelling these {selectedCount} jobs.
                This reason will be applied to all selected jobs.
              </p>

              <div className="mb-4">
                <label htmlFor="cancelReason" className="block text-sm font-medium text-neutral-700 mb-2">
                  Cancellation Reason
                </label>
                <textarea
                  id="cancelReason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter reason for cancellation..."
                />
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelReason('');
                  }}
                  className="btn-secondary"
                  disabled={bulkCancel.isPending}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBulkCancel}
                  className="btn-primary bg-red-600 hover:bg-red-700"
                  disabled={bulkCancel.isPending || !cancelReason.trim()}
                >
                  {bulkCancel.isPending ? 'Cancelling...' : `Cancel ${selectedCount} Jobs`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
